import { FC } from "react";
import { Box, Typography } from "@material-ui/core";
import { FormikBag, FormikProps, withFormik } from "formik";
import * as yup from "yup";

import {
  Button,
  LabelField,
  MarketplaceMedianLabelField,
  NumberField,
  UsdIntegerInput,
} from "components";
import { getTrailingYield } from "lib/finance";
import { formatCurrencyAmount } from "lib/format";
import { parseIntegerAmount, parseNumber } from "lib/parse";
import { BaseAmountSource } from "services/listings/types";
import { Proxy } from "services/proxies/types";
import theme from "theme";

export interface FormValues {
  amount: string;
}

interface FormProps {
  onSave: (values: FormValues) => Promise<void>;
  initialValues: FormValues;
  isLoading: boolean;
  marketplaceMedianSource: BaseAmountSource;
  marketplaceMedianMultiplier: number;
  marketplaceMedian: string | null;
  buyNowPrice: number;
  highestOpenBuyerOfferAmount: number;
  isMarketplaceMedianDisabled: boolean;
  listingProxyIncrementAmount: number;
  ltm: number;
  proxy: Proxy | undefined;
}

const CreateEditProxyForm: FC<FormProps & FormikProps<FormValues>> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
  isLoading,
  marketplaceMedianSource,
  marketplaceMedianMultiplier,
  marketplaceMedian,
  isMarketplaceMedianDisabled,
  listingProxyIncrementAmount,
  ltm,
  proxy,
}) => {
  const amount = parseNumber(values.amount, 0);

  return (
    <form onSubmit={handleSubmit}>
      <Typography component="div" gutterBottom>
        <Box fontSize={13} textAlign="center">
          {!!proxy
            ? "Update your proxy amount below"
            : "Create a proxy offer below"}
        </Box>
      </Typography>
      <Box my={2} textAlign="center">
        {!isMarketplaceMedianDisabled && (
          <Box my={2}>
            <MarketplaceMedianLabelField
              marketplaceMedian={marketplaceMedian}
              marketplaceMedianMultiplier={marketplaceMedianMultiplier}
              marketplaceMedianSource={marketplaceMedianSource}
            />
          </Box>
        )}
        <Box my={2}>
          <NumberField
            name="amount"
            type="text"
            fullWidth
            label="Proxy Amount"
            tooltipText={
              <>
                <Box>
                  This is the maximum amount you will offer on this listing.
                  This proxy will compete against other bidders but will not
                  interact with the listing owner’s counteroffers. (You will
                  need to manually interact with those.)
                </Box>
                <Box>
                  Your proxy will immediately outbid other investor’s offers. If
                  your offer is rejected or expires, an offer one increment
                  higher will be placed the following day. This will continue
                  until an offer is accepted or your proxy amount is reached.{" "}
                  <span
                    style={{ fontWeight: theme.typography.fontWeightMedium }}
                  >
                    Offers placed by your proxy can not be withdrawn.
                  </span>
                </Box>
              </>
            }
            placeholder=""
            value={parseIntegerAmount(values.amount)}
            helperText={
              touched.amount && !!errors.amount
                ? errors.amount
                : `Trailing Earning Yield: ${getTrailingYield(ltm, amount)}%`
            }
            disabled={isSubmitting}
            onBlur={handleBlur}
            onChange={handleChange}
            error={touched.amount && !!errors.amount}
            inputComponent={UsdIntegerInput}
          />
        </Box>
        <Box my={2}>
          <LabelField
            label="Increase Increment"
            value={formatCurrencyAmount("USD", listingProxyIncrementAmount)}
            tooltipText="Your offer amount will increase by this amount."
          />
        </Box>
        <Box my={2}>
          <Box my={2}>
            <Button
              customColor="green"
              size="large"
              variant="contained"
              type="submit"
              fullWidth
              disabled={isSubmitting || isLoading}
              id="edit-proxy"
            >
              {!!proxy ? "Edit Proxy" : "Create Proxy"}
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
};

const formikOptions = {
  mapPropsToValues: (props: FormProps) => props.initialValues,

  validationSchema: (props: FormProps) => {
    const { buyNowPrice, highestOpenBuyerOfferAmount, proxy } = props;
    return yup.object().shape({
      amount: yup
        .string()
        .test("same", "Must update proxy amount", function (amount) {
          const amountDifferent =
            amount && parseNumber(amount, 0) === parseNumber(proxy?.amount, 0)
              ? false
              : true;

          return amountDifferent;
        })
        .test(
          "max",
          `The proxy amount must be less than the list price (${formatCurrencyAmount(
            "USD",
            buyNowPrice,
          )})`,
          amount =>
            !amount || parseNumber(amount, 0) < (buyNowPrice || Infinity),
        )
        .test(
          "greaterThanHighestOpenBuyerOfferAmount",
          "The proxy amount must be greater than the highest open offer",
          proxyAmount =>
            !proxyAmount ||
            parseNumber(proxyAmount, 0) > highestOpenBuyerOfferAmount,
        )
        .required("Required"),
    });
  },

  handleSubmit: async (
    values: FormValues,
    { props, setSubmitting }: FormikBag<FormProps, FormValues>,
  ) => {
    try {
      await props.onSave(values);
    } catch {
      // pass
    } finally {
      setSubmitting(false);
    }
  },
};

const EnhancedEditProxyForm = withFormik<FormProps, FormValues>(formikOptions)(
  CreateEditProxyForm,
);

export default EnhancedEditProxyForm;
